<template>
  <div class="cart-container">
    <!-- 顶部导航 -->
    <div class="nav-bar">
      <div class="nav-content">
        <div class="logo">
          <span class="glow-text">云商城</span>
        </div>
        <div class="nav-menu">
          <el-menu mode="horizontal" default-active="4" class="nav-menu-inner">
            <el-menu-item index="1" @click="goToHome">首页</el-menu-item>
            <el-menu-item index="4">购物车</el-menu-item>
          </el-menu>
        </div>
        <div class="user-info">
          <el-dropdown @command="handleCommand">
            <span class="el-dropdown-link">
              {{ userNickname || '用户' }}
              <el-icon class="el-icon--right"><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="home">返回首页</el-dropdown-item>
                <el-dropdown-item command="profile">个人资料</el-dropdown-item>
                <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-content">
      <!-- 页面标题 -->
      <div class="page-header glassmorphism">
        <div class="page-header-left">
          <el-icon :size="28" color="#537ce5"><ShoppingCart /></el-icon>
          <div>
            <h1>我的购物车</h1>
            <span class="header-tip">共 {{ cartList.length }} 件商品</span>
          </div>
        </div>
        <button class="btn-back" @click="goToHome">
          <el-icon><ArrowLeft /></el-icon>
          继续购物
        </button>
      </div>

      <!-- 购物车有商品 -->
      <template v-if="cartList.length > 0">
        <!-- 全选栏 -->
        <div class="cart-toolbar glassmorphism">
          <el-checkbox v-model="isAllChecked" @change="toggleSelectAll">全选</el-checkbox>
          <button class="btn-delete-batch" @click="batchRemove" :disabled="selectedItems.length === 0">
            <el-icon><Delete /></el-icon>
            删除选中 ({{ selectedItems.length }})
          </button>
        </div>

        <!-- 商品列表 -->
        <div class="cart-list">
          <div v-for="item in cartList" :key="item.id" class="cart-item glassmorphism">
            <div class="item-checkbox">
              <el-checkbox v-model="item.checked" @change="onItemCheckChange" />
            </div>
            <div class="item-image">
              <el-image :src="getProductImage(item.picUrl, item)" fit="cover" class="item-img">
                <template #error>
                  <div class="image-placeholder">
                    <el-icon :size="32" color="#ccc"><Goods /></el-icon>
                  </div>
                </template>
              </el-image>
            </div>
            <div class="item-info">
              <h3 class="item-title">{{ item.title || item.name }}</h3>
              <div class="item-meta">
                <el-tag size="small" v-if="item.category">{{ item.category }}</el-tag>
                <span class="item-stock" :class="{ 'low-stock': item.stock < 10 }">
                  库存: {{ item.stock ?? '充足' }}
                </span>
              </div>
            </div>
            <div class="item-price">
              <span class="price-symbol">¥</span>
              <span class="price-value">{{ item.price }}</span>
            </div>
            <div class="item-quantity">
              <el-input-number 
                v-model="item.quantity" 
                :min="1" 
                :max="item.stock || 99" 
                size="small"
                @change="onQuantityChange(item)"
              />
            </div>
            <div class="item-subtotal">
              <span class="subtotal-label">小计</span>
              <span class="subtotal-value">¥{{ (item.price * item.quantity).toFixed(2) }}</span>
            </div>
            <div class="item-actions">
              <button class="btn-remove" @click="removeItem(item)">
                <el-icon><Delete /></el-icon>
              </button>
            </div>
          </div>
        </div>

        <!-- 结算栏 -->
        <div class="cart-footer glassmorphism">
          <div class="footer-left">
            <el-checkbox v-model="isAllChecked" @change="toggleSelectAll">全选</el-checkbox>
            <button class="btn-delete-batch-sm" @click="batchRemove" :disabled="selectedItems.length === 0">
              删除选中
            </button>
          </div>
          <div class="footer-right">
            <div class="total-info">
              <span class="total-count">
                已选 <strong>{{ selectedItems.length }}</strong> 件商品
              </span>
              <span class="total-price">
                合计：<span class="total-value">¥{{ totalPrice }}</span>
              </span>
            </div>
            <button class="btn-checkout" :disabled="selectedItems.length === 0 || checkoutLoading" @click="checkout" :class="{ 'is-loading': checkoutLoading }">
              <span v-if="!checkoutLoading">去结算 ({{ selectedItems.length }})</span>
              <span v-else>处理中...</span>
            </button>
          </div>
        </div>
      </template>

      <!-- 购物车为空 -->
      <template v-else>
        <div class="empty-cart glassmorphism">
          <div class="empty-icon-wrapper">
            <el-icon :size="80" color="#d0d0d0"><ShoppingCart /></el-icon>
          </div>
          <h2>购物车是空的</h2>
          <p>快去挑选心仪的商品吧！</p>
          <button class="btn-go-shopping" @click="goToHome">
            <el-icon><ShoppingBag /></el-icon>
            去逛逛
          </button>
        </div>
      </template>
    </div>

    <!-- 底部 -->
    <div class="footer">
      <div class="footer-content">
        <div class="footer-section">
          <h4>关于我们</h4>
          <p>云商城是一个基于微服务架构的现代化交易平台。</p>
        </div>
        <div class="footer-section">
          <h4>快速链接</h4>
          <ul>
            <li><a href="#">帮助中心</a></li>
            <li><a href="#">联系我们</a></li>
            <li><a href="#">服务条款</a></li>
          </ul>
        </div>
        <div class="footer-section">
          <h4>联系方式</h4>
          <p>Email: support@cloudshop.com</p>
          <p>Phone: 400-800-8888</p>
        </div>
      </div>
      <div class="footer-bottom">
        <p>&copy; 2026 云商城 - 微服务交易平台. All rights reserved.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElNotification, ElMessageBox } from 'element-plus'
import { ArrowDown, ArrowLeft, ShoppingCart, ShoppingBag, Delete, Goods } from '@element-plus/icons-vue'
import { cartService, orderService, getCurrentUserId } from '../api/order'

const router = useRouter()
const userNickname = ref('')
const cartList = ref([])
const loadingCart = ref(false)
const checkoutLoading = ref(false)
const defaultImage = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"><rect fill="%23f0f2f5" width="300" height="300"/><text x="150" y="145" text-anchor="middle" fill="%23bbb" font-size="16" font-family="sans-serif">暂无图片</text><path d="M130 170h40l-8-12-7 9-5-4z" fill="%23ccc"/><circle cx="140" cy="155" r="4" fill="%23ccc"/></svg>')}`

const productImageMap = {
  'iphone': 'https://cdn-icons-png.flaticon.com/512/0/191.png',
  'mate': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'galaxy': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'pixel': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'xiaomi': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'oppo': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'vivo': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'redmi': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  '手机': 'https://cdn-icons-png.flaticon.com/512/0/191.png',
  'macbook': 'https://cdn-icons-png.flaticon.com/512/2004/2004580.png',
  'thinkpad': 'https://cdn-icons-png.flaticon.com/512/2004/2004580.png',
  '笔记本': 'https://cdn-icons-png.flaticon.com/512/2004/2004580.png',
  '电脑': 'https://cdn-icons-png.flaticon.com/512/2004/2004580.png',
  'ipad': 'https://cdn-icons-png.flaticon.com/512/2329/2329087.png',
  '平板': 'https://cdn-icons-png.flaticon.com/512/2329/2329087.png',
  'airpods': 'https://cdn-icons-png.flaticon.com/512/2652/2652218.png',
  '耳机': 'https://cdn-icons-png.flaticon.com/512/2652/2652218.png',
  '音箱': 'https://cdn-icons-png.flaticon.com/512/3659/3659784.png',
  '音响': 'https://cdn-icons-png.flaticon.com/512/3659/3659784.png',
  '手表': 'https://cdn-icons-png.flaticon.com/512/2972/2972531.png',
  'watch': 'https://cdn-icons-png.flaticon.com/512/2972/2972531.png',
  '手环': 'https://cdn-icons-png.flaticon.com/512/2972/2972531.png',
  '相机': 'https://cdn-icons-png.flaticon.com/512/2956/2956744.png',
  '摄像': 'https://cdn-icons-png.flaticon.com/512/2956/2956744.png',
  '冰箱': 'https://cdn-icons-png.flaticon.com/512/3062/3062634.png',
  '洗衣机': 'https://cdn-icons-png.flaticon.com/512/3062/3062621.png',
  '空调': 'https://cdn-icons-png.flaticon.com/512/3062/3062626.png',
  '电视': 'https://cdn-icons-png.flaticon.com/512/2847/2847039.png',
  '微波炉': 'https://cdn-icons-png.flaticon.com/512/3062/3062649.png',
  '衣': 'https://cdn-icons-png.flaticon.com/512/2503/2503380.png',
  '裤': 'https://cdn-icons-png.flaticon.com/512/2503/2503387.png',
  '鞋': 'https://cdn-icons-png.flaticon.com/512/2742/2742528.png',
  '帽': 'https://cdn-icons-png.flaticon.com/512/2503/2503266.png',
  '服装': 'https://cdn-icons-png.flaticon.com/512/2503/2503380.png',
  '口红': 'https://cdn-icons-png.flaticon.com/512/1940/1940922.png',
  '面膜': 'https://cdn-icons-png.flaticon.com/512/2553/2553695.png',
  '护肤': 'https://cdn-icons-png.flaticon.com/512/2553/2553695.png',
  '香水': 'https://cdn-icons-png.flaticon.com/512/2553/2553642.png',
  '美妆': 'https://cdn-icons-png.flaticon.com/512/1940/1940922.png',
  '零食': 'https://cdn-icons-png.flaticon.com/512/2515/2515183.png',
  '饮料': 'https://cdn-icons-png.flaticon.com/512/2405/2405479.png',
  '茶': 'https://cdn-icons-png.flaticon.com/512/2935/2935406.png',
  '咖啡': 'https://cdn-icons-png.flaticon.com/512/924/924514.png',
  '食品': 'https://cdn-icons-png.flaticon.com/512/1046/1046784.png',
  '跑步': 'https://cdn-icons-png.flaticon.com/512/2742/2742528.png',
  '篮球': 'https://cdn-icons-png.flaticon.com/512/484/484482.png',
  '健身': 'https://cdn-icons-png.flaticon.com/512/2936/2936886.png',
  '运动': 'https://cdn-icons-png.flaticon.com/512/2936/2936886.png',
  '户外': 'https://cdn-icons-png.flaticon.com/512/2936/2936886.png',
  '书': 'https://cdn-icons-png.flaticon.com/512/2232/2232688.png',
  '图书': 'https://cdn-icons-png.flaticon.com/512/2232/2232688.png',
  '游戏': 'https://cdn-icons-png.flaticon.com/512/686/686589.png',
  'switch': 'https://cdn-icons-png.flaticon.com/512/686/686589.png',
  'ps5': 'https://cdn-icons-png.flaticon.com/512/686/686589.png',
  '键盘': 'https://cdn-icons-png.flaticon.com/512/2381/2381590.png',
  '鼠标': 'https://cdn-icons-png.flaticon.com/512/2381/2381573.png',
  '手机数码': 'https://cdn-icons-png.flaticon.com/512/0/191.png',
  '电脑办公': 'https://cdn-icons-png.flaticon.com/512/2004/2004580.png',
  '家用电器': 'https://cdn-icons-png.flaticon.com/512/2847/2847039.png',
  '服装鞋帽': 'https://cdn-icons-png.flaticon.com/512/2503/2503380.png',
  '美妆个护': 'https://cdn-icons-png.flaticon.com/512/1940/1940922.png',
  '食品饮料': 'https://cdn-icons-png.flaticon.com/512/1046/1046784.png',
  '运动户外': 'https://cdn-icons-png.flaticon.com/512/2936/2936886.png',
  '图书音像': 'https://cdn-icons-png.flaticon.com/512/2232/2232688.png',
}

/** 获取商品图片 */
const getProductImage = (picUrl, item) => {
  if (item) {
    const searchText = `${item.title || ''}${item.name || ''}${item.category || ''}`.toLowerCase()
    for (const [keyword, imgUrl] of Object.entries(productImageMap)) {
      if (searchText.includes(keyword.toLowerCase())) {
        return imgUrl
      }
    }
  }

  if (
    picUrl &&
    typeof picUrl === 'string' &&
    picUrl.startsWith('http') &&
    !picUrl.includes('picsum') &&
    !picUrl.includes('placeholder') &&
    !picUrl.includes('unsplash') &&
    !picUrl.includes('lorem') &&
    !picUrl.includes('random') &&
    !picUrl.includes('loremflickr') &&
    !picUrl.includes('example.com') &&
    !picUrl.includes('via.placeholder')
  ) {
    return picUrl
  }

  return defaultImage
}

/** 获取用户 ID */
const getUserId = () => {
  const uid = getCurrentUserId()
  if (!uid) {
    ElMessage.warning('请先登录')
    router.push('/login')
  }
  return uid
}

/** 选中的商品 */
const selectedItems = computed(() => cartList.value.filter(item => item.checked))

/** 是否全选 */
const isAllChecked = computed({
  get: () => cartList.value.length > 0 && cartList.value.every(item => item.checked),
  set: () => {} // 由 toggleSelectAll 处理
})

/** 合计价格 */
const totalPrice = computed(() => {
  return selectedItems.value
    .reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0)
    .toFixed(2)
})

/** 全选 / 取消全选 */
const toggleSelectAll = (val) => {
  cartList.value.forEach(item => { item.checked = val })
}

/** 单个选中变化 */
const onItemCheckChange = () => {}

/** 数量变化 — 调用 /cart/update */
const onQuantityChange = async (item) => {
  try {
    await cartService.updateCart({ id: item.cartId || item.id, count: item.quantity })
    ElMessage.success(`${item.title || item.name} 数量已更新`)
  } catch (error) {
    console.error('更新数量失败:', error)
    ElMessage.warning('数量已在本地更新')
  }
}

/** 删除单个商品 — 调用 /cart/delete/{id} */
const removeItem = async (item) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除「${item.title || item.name}」吗？`,
      '删除确认',
      { confirmButtonText: '删除', cancelButtonText: '取消', type: 'warning' }
    )
    try {
      await cartService.deleteCart(item.cartId || item.id)
    } catch (e) {
      console.error('后端删除失败，本地删除:', e)
    }
    cartList.value = cartList.value.filter(i => (i.cartId || i.id) !== (item.cartId || item.id))
    syncLocalCart()
    ElNotification({
      title: '已删除',
      message: `${item.title || item.name} 已从购物车移除`,
      type: 'success',
      duration: 1500,
    })
  } catch { /* 用户取消 */ }
}

/** 批量删除 */
const batchRemove = async () => {
  if (selectedItems.value.length === 0) return
  try {
    await ElMessageBox.confirm(
      `确定要删除选中的 ${selectedItems.value.length} 件商品吗？`,
      '批量删除',
      { confirmButtonText: '删除', cancelButtonText: '取消', type: 'warning' }
    )
    // 逐个调用后端删除
    for (const item of selectedItems.value) {
      try {
        await cartService.deleteCart(item.cartId || item.id)
      } catch (e) { console.error('删除失败:', e) }
    }
    const selectedIds = new Set(selectedItems.value.map(i => i.cartId || i.id))
    cartList.value = cartList.value.filter(i => !selectedIds.has(i.cartId || i.id))
    syncLocalCart()
    ElNotification({
      title: '批量删除完成',
      message: `已删除 ${selectedIds.size} 件商品`,
      type: 'success',
      duration: 1500,
    })
  } catch { /* 用户取消 */ }
}

/** 去结算 — 遍历选中商品，逐个调用 /order/create */
const checkout = async () => {
  if (selectedItems.value.length === 0) {
    ElMessage.warning('请至少选中一件商品')
    return
  }
  const userId = getUserId()
  if (!userId) return

  try {
    await ElMessageBox.confirm(
      `即将结算 ${selectedItems.value.length} 件商品，合计 ¥${totalPrice.value}`,
      '确认结算',
      { confirmButtonText: '确认', cancelButtonText: '取消', type: 'info' }
    )
  } catch { return /* 取消 */ }

  checkoutLoading.value = true
  try {
    let successCount = 0
    const failedItems = []

    // 遍历选中商品，逐个下单
    for (const item of selectedItems.value) {
      try {
        const orderData = {
          userId,
          productId: item.productId || item.id,
          count: item.quantity,
          totalPrice: (item.price || 0) * (item.quantity || 1)
        }
        await orderService.createOrder(orderData)
        
        // 下单成功后删除购物车项
        try {
          await cartService.deleteCart(item.cartId || item.id)
        } catch (e) {
          console.error('删除购物车项失败:', e)
        }
        
        successCount++
      } catch (error) {
        console.error(`商品 ${item.title} 下单失败:`, error)
        failedItems.push(item.title || item.name)
      }
    }

    // 本地同步：移除已下单的商品
    const selectedIds = new Set(selectedItems.value.map(i => i.cartId || i.id))
    cartList.value = cartList.value.filter(i => !selectedIds.has(i.cartId || i.id))
    syncLocalCart()

    if (successCount > 0) {
      ElNotification({
        title: '下单成功',
        message: `成功下单 ${successCount} 件商品，请前往支付！`,
        type: 'success',
        duration: 2500,
      })
      router.push('/orders')
    } else {
      ElMessage.error('所有商品下单失败，请稍后重试')
    }

    if (failedItems.length > 0) {
      ElMessage.warning(`${failedItems.length} 件商品下单失败：${failedItems.join('、')}`)
    }
  } catch (error) {
    console.error('结算失败:', error)
    ElMessage.error('结算失败，请稍后重试')
  } finally {
    checkoutLoading.value = false
  }
}

/** 回退方案：用 localStorage 创建订单 */
const fallbackCheckout = () => {
  const items = selectedItems.value.map(i => ({
    id: i.productId || i.id,
    title: i.title || i.name,
    name: i.name,
    price: i.price,
    picUrl: i.picUrl,
    stock: i.stock,
    category: i.category,
    quantity: i.quantity,
  }))
  const totalQty = items.reduce((s, i) => s + i.quantity, 0)
  const now = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
  const rand = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
  const order = {
    id: Date.now(),
    orderNo: `OD${ts}${rand}`,
    createTime: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`,
    status: 'pending',
    items,
    totalQuantity: totalQty,
    totalAmount: totalPrice.value,
  }
  let orders = []
  try { orders = JSON.parse(localStorage.getItem('orders') || '[]') } catch { orders = [] }
  orders.unshift(order)
  localStorage.setItem('orders', JSON.stringify(orders))

  const selectedIds = new Set(selectedItems.value.map(i => i.cartId || i.id))
  cartList.value = cartList.value.filter(i => !selectedIds.has(i.cartId || i.id))
  syncLocalCart()

  ElNotification({ title: '订单提交成功（本地）', message: '订单号：' + order.orderNo, type: 'success', duration: 2500 })
  router.push('/orders')
}

/** 同步购物车到 localStorage（给悬浮工具栏用） */
const syncLocalCart = () => {
  localStorage.setItem('cart', JSON.stringify(cartList.value))
}

/** 从后端加载购物车 */
const loadCart = async () => {
  const userId = getCurrentUserId()
  if (!userId) {
    // 从 localStorage 回退
    loadCartFromLocal()
    return
  }

  loadingCart.value = true
  try {
    const res = await cartService.getCartList(userId)
    const data = res.data?.data || res.data?.records || res.data
    if (Array.isArray(data) && data.length > 0) {
      cartList.value = data.map(item => {
        // 兼容后端返回字段：可能有 productDetail 嵌套
        const detail = item.productDetail || item.product || {}
        return {
          cartId: item.id,
          id: item.productId || item.id,
          productId: item.productId,
          title: detail.title || detail.name || item.productName || item.title || '未知商品',
          name: detail.name || item.productName || item.name,
          price: detail.price ?? item.price ?? 0,
          picUrl: detail.picUrl || item.picUrl,
          stock: detail.stock ?? item.stock ?? 99,
          category: detail.category || item.category,
          quantity: item.quantity || 1,
          checked: false,
        }
      })
      syncLocalCart()
    } else {
      // 后端为空则检查 localStorage
      loadCartFromLocal()
    }
  } catch (error) {
    console.error('从后端加载购物车失败，回退到本地:', error)
    loadCartFromLocal()
  } finally {
    loadingCart.value = false
  }
}

/** 从 localStorage 加载购物车 */
const loadCartFromLocal = () => {
  try {
    const data = localStorage.getItem('cart')
    if (data) {
      cartList.value = JSON.parse(data).map(item => ({
        ...item,
        checked: item.checked ?? false,
        quantity: item.quantity || 1,
      }))
    }
  } catch (e) {
    console.error('加载本地购物车失败:', e)
    cartList.value = []
  }
}

/** 返回首页 */
const goToHome = () => router.push('/home')

/** 用户下拉菜单 */
const handleCommand = (command) => {
  if (command === 'logout') {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    ElNotification({ title: '再见', message: '已成功退出登录', type: 'info', duration: 1800 })
    router.push('/login')
  } else if (command === 'home') {
    goToHome()
  } else if (command === 'profile') {
    router.push('/profile')
  }
}

onMounted(() => {
  const userStr = localStorage.getItem('user')
  if (userStr) {
    try {
      const user = JSON.parse(userStr)
      userNickname.value = user.nickname || user.username || '用户'
    } catch (e) {
      console.error('解析用户信息失败:', e)
    }
  }
  loadCart()
})
</script>

<style scoped>
.cart-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #f1f4f8 100%);
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

/* ========== 导航栏 ========== */
.nav-bar {
  background: linear-gradient(135deg, #232a4d 0%, #4b2067 100%);
  box-shadow: 0 2px 8px #0000150d;
  position: sticky;
  top: 0;
  z-index: 1000;
  backdrop-filter: blur(10px);
}

.nav-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo {
  font-size: 24px;
  font-weight: bold;
  margin-right: 40px;
  min-width: 120px;
}

.glow-text {
  color: #fff;
  text-shadow: 0 0 8px #a3a3ff, 0 0 16px #4b2067;
}

.nav-menu { flex: 1; }

.nav-menu-inner {
  background: transparent !important;
  border-bottom: none !important;
}

.nav-menu-inner :deep(.el-menu-item) {
  color: #fff !important;
  background: transparent !important;
  font-size: 0.95rem;
}

.nav-menu-inner :deep(.el-menu-item:hover) {
  background: rgba(255, 255, 255, 0.1) !important;
}

.nav-menu-inner :deep(.el-menu-item.is-active) {
  border-bottom-color: #a3a3ff !important;
}

.user-info {
  margin-left: auto;
  color: #fff;
  font-size: 14px;
}

.user-info :deep(.el-dropdown-link) {
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-info :deep(.el-dropdown-link:hover) {
  color: #a3a3ff;
}

/* ========== 主内容区 ========== */
.main-content {
  flex: 1;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  padding: 24px 16px 40px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ========== 磨砂玻璃 ========== */
.glassmorphism {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.08);
}

/* ========== 页面标题 ========== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
}

.page-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.page-header-left h1 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #232a4d;
  margin: 0;
}

.header-tip {
  font-size: 0.85rem;
  color: #999;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #232a4d 0%, #4b2067 100%);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-back:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(35, 42, 77, 0.35);
}

/* ========== 全选工具栏 ========== */
.cart-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 28px;
}

.btn-delete-batch {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 16px;
  border: 1px solid #ff4d4f;
  border-radius: 8px;
  background: transparent;
  color: #ff4d4f;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
}

.btn-delete-batch:hover:not(:disabled) {
  background: #ff4d4f;
  color: #fff;
}

.btn-delete-batch:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ========== 商品列表 ========== */
.cart-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cart-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  transition: all 0.3s ease;
}

.cart-item:hover {
  box-shadow: 0 8px 28px rgba(83, 124, 229, 0.12);
  transform: translateY(-2px);
}

.item-checkbox {
  flex-shrink: 0;
}

.item-image {
  flex-shrink: 0;
  width: 90px;
  height: 90px;
  border-radius: 12px;
  overflow: hidden;
}

.item-img {
  width: 90px;
  height: 90px;
}

.image-placeholder {
  width: 90px;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.item-title {
  font-size: 1rem;
  font-weight: 600;
  color: #232a4d;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.item-stock {
  font-size: 0.8rem;
  color: #555;
  font-weight: 500;
}

.item-stock.low-stock {
  color: #ff4d4f;
  font-weight: bold;
}

.item-price {
  flex-shrink: 0;
  width: 90px;
  text-align: center;
}

.price-symbol {
  font-size: 0.85rem;
  color: #ff4d4f;
  font-weight: 600;
}

.price-value {
  font-size: 1.15rem;
  color: #ff4d4f;
  font-weight: 700;
}

.item-quantity {
  flex-shrink: 0;
  width: 130px;
  display: flex;
  justify-content: center;
}

.item-quantity :deep(.el-input-number) {
  width: 110px;
}

.item-quantity :deep(.el-input-number .el-input__wrapper) {
  border-radius: 8px;
}

.item-subtotal {
  flex-shrink: 0;
  width: 100px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.subtotal-label {
  font-size: 0.75rem;
  color: #999;
}

.subtotal-value {
  font-size: 1.05rem;
  font-weight: 700;
  color: #ff4d4f;
}

.item-actions {
  flex-shrink: 0;
}

.btn-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: rgba(255, 77, 79, 0.08);
  color: #ff4d4f;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.25s ease;
}

.btn-remove:hover {
  background: #ff4d4f;
  color: #fff;
  transform: scale(1.1);
}

/* ========== 结算栏 ========== */
.cart-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 28px;
  position: sticky;
  bottom: 0;
  z-index: 100;
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.btn-delete-batch-sm {
  border: none;
  background: transparent;
  color: #ff4d4f;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background 0.2s;
}

.btn-delete-batch-sm:hover:not(:disabled) {
  background: rgba(255, 77, 79, 0.08);
}

.btn-delete-batch-sm:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.footer-right {
  display: flex;
  align-items: center;
  gap: 24px;
}

.total-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.total-count {
  font-size: 0.85rem;
  color: #666;
}

.total-count strong {
  color: #537ce5;
}

.total-price {
  font-size: 0.95rem;
  color: #333;
}

.total-value {
  font-size: 1.4rem;
  font-weight: 800;
  color: #ff4d4f;
}

.btn-checkout {
  padding: 12px 36px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ff4d4f 100%);
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 1px;
}

.btn-checkout:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(255, 77, 79, 0.4);
}

.btn-checkout:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #ccc;
}

.btn-checkout.is-loading {
  opacity: 0.8;
  cursor: wait;
  pointer-events: none;
}

/* ========== 空购物车 ========== */
.empty-cart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  gap: 12px;
}

.empty-icon-wrapper {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f0f0f5 0%, #e8e8f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.empty-cart h2 {
  font-size: 1.25rem;
  color: #333;
  margin: 0;
}

.empty-cart p {
  font-size: 0.95rem;
  color: #999;
  margin: 0;
}

.btn-go-shopping {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 32px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #537ce5 0%, #8b5fc9 100%);
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 8px;
}

.btn-go-shopping:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(83, 124, 229, 0.4);
}

/* ========== 底部 ========== */
.footer {
  background: #232a4d;
  color: #fff;
  margin-top: auto;
  padding: 30px 20px 15px;
}

.footer-content {
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-bottom: 25px;
}

.footer-section h4 { margin-bottom: 16px; font-size: 1.1rem; }
.footer-section p { font-size: 0.9rem; opacity: 0.8; margin-bottom: 8px; }
.footer-section ul { list-style: none; padding: 0; }
.footer-section li { margin-bottom: 8px; }
.footer-section a { color: #a3a3ff; text-decoration: none; transition: color 0.3s; }
.footer-section a:hover { color: #fff; }

.footer-bottom {
  border-top: 1px solid #4b206730;
  padding-top: 20px;
  text-align: center;
  font-size: 0.85rem;
  opacity: 0.7;
}

/* ========== 响应式 ========== */
@media (max-width: 768px) {
  .cart-item {
    flex-wrap: wrap;
    gap: 12px;
    padding: 14px 16px;
  }

  .item-image {
    width: 70px;
    height: 70px;
  }

  .item-img,
  .image-placeholder {
    width: 70px;
    height: 70px;
  }

  .item-info {
    width: calc(100% - 110px);
    flex: none;
  }

  .item-price,
  .item-quantity,
  .item-subtotal {
    width: auto;
  }

  .item-price { order: 1; }
  .item-quantity { order: 2; }
  .item-subtotal { order: 3; flex-direction: row; gap: 6px; }
  .item-actions { order: 4; }

  .cart-footer {
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }

  .footer-right {
    width: 100%;
    justify-content: space-between;
  }

  .page-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .nav-content {
    flex-wrap: wrap;
    gap: 12px;
  }

  .nav-menu { width: 100%; }
  .user-info { width: 100%; margin-left: 0; }
}
</style>
